import {
  Component,
  Input,
  forwardRef,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnDestroy,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  AbstractControl,
} from "@angular/forms";
import { FormErrorComponent } from "../form-error/form-error.component";

export type DatepickerMode = "single" | "range" | "multiple";

@Component({
  selector: "is-datepicker-field",
  standalone: true,
  imports: [CommonModule, FormErrorComponent],
  templateUrl: "./datepicker-field.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerFieldComponent),
      multi: true,
    },
  ],
})
export class DatepickerFieldComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  @Input() label = "";
  @Input() placeholder = "dd/mm/aaaa";
  @Input() required = false;
  @Input() helpText = "";
  @Input() mode: DatepickerMode = "single";
  @Input() minDate = "";
  @Input() maxDate = "";
  @Input() control: AbstractControl | null = null;

  @ViewChild("dateInput") dateInputRef!: ElementRef<HTMLInputElement>;

  value = "";
  isDisabled = false;
  private flatpickrInstance: any = null;

  private onChange: (val: string) => void = () => {};
  protected onTouched: () => void = () => {};

  get hasError(): boolean {
    return !!(
      this.control?.invalid &&
      (this.control?.dirty || this.control?.touched)
    );
  }

  ngAfterViewInit(): void {
    // Initializes flatpickr if available in the global scope (loaded by the host app)
    if (typeof (window as any)["flatpickr"] !== "undefined") {
      this.flatpickrInstance = (window as any)["flatpickr"](
        this.dateInputRef.nativeElement,
        {
          mode: this.mode,
          dateFormat: "d/m/Y",
          minDate: this.minDate || undefined,
          maxDate: this.maxDate || undefined,
          locale: "pt",
          allowInput: true,
          onChange: (selectedDates: Date[], dateStr: string) => {
            this.value = dateStr;
            this.onChange(dateStr);
            this.onTouched();
          },
        },
      );
    }
  }

  writeValue(val: string): void {
    this.value = val ?? "";
    if (this.flatpickrInstance) {
      this.flatpickrInstance.setDate(val, false);
    }
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
    if (this.flatpickrInstance) {
      disabled ? this.flatpickrInstance.destroy() : null;
    }
  }

  handleNativeChange(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.value = val;
    this.onChange(val);
    this.onTouched();
  }

  ngOnDestroy(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
  }
}
